import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from 'services/api';
import { Box, Button, Checkbox, IconButton, ResponsiveDialog, Stack, styled, Text } from 'shell/components';
import {
    convertCanvasRubric,
    convertD2LRubric,
    isUnscoredRubric,
    usesCompleteIncompleteGrading,
} from 'utils/rubricScoring';

const PushRubricDialog = ({ open, onClose, onSave, form }) => {
    const { device } = useViewport();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [checked, setChecked] = useState(true);
    const [loading, setLoading] = useState(false);
    const canUseCompletionFallback =
        process.env.REACT_APP_KIND === 'CANVAS' && isUnscoredRubric(form) && usesCompleteIncompleteGrading(form);
    const requiresRubricSync = isUnscoredRubric(form) && !canUseCompletionFallback;

    useEffect(() => {
        if (open) setChecked(true);
    }, [open]);

    const handleClose = () => {
        onClose();
    };

    const handleClickSave = async () => {
        setLoading(true);
        try {
            const accessToken = localStorage.getItem('access_token');
            let pushedRubric = null;
            let newAPIData = null;

            if (checked || requiresRubricSync) {
                const pushRequest = await apiService.pushRubric({ accessToken, ...form });
                if (process.env.REACT_APP_KIND === 'CANVAS') {
                    newAPIData = await apiService.getAssignment({
                        course_id: form.api_data.course_id,
                        accessToken,
                        assignment_id: form.api_assignment_id,
                    });
                    const criteria = newAPIData?.rubric ?? pushRequest?.rubric?.criteria;
                    const settings = newAPIData?.rubric_settings ?? pushRequest?.rubric;
                    pushedRubric = criteria ? convertCanvasRubric(criteria, settings) : form.rubric;
                } else if (process.env.REACT_APP_KIND === 'D2L') {
                    const rubric =
                        pushRequest?.Assessment?.Rubrics?.[0] ??
                        (pushRequest?.rubric?.CriteriaGroups ? pushRequest.rubric : null) ??
                        (pushRequest?.CriteriaGroups ? pushRequest : null) ??
                        form.api_data?.Assessment?.Rubrics?.[0];
                    pushedRubric = rubric ? convertD2LRubric(rubric) : form.rubric;
                    newAPIData = form.api_data;
                }
            } else {
                pushedRubric = form.rubric;
                newAPIData = form.api_data;
            }
            onSave({ api_data: newAPIData, rubric: pushedRubric });
        } catch (error) {
            console.error('Failed to sync rubric to LMS:', error);
            message.error(t('rubric.push-lms.sync-failed', 'Rubric sync failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <TitleStack spacing={2}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'} weight={'medium'}>
                        {t('rubric.push-lms.sync-rubric', 'Sync Rubric To Your LMS?')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
                <Text weight={'regular'}>
                    {t(
                        'rubric.push-lms.difference-note',
                        'There are differences between the assignment rubric on your LMS and TimelyGrader. If you sync TimelyGrader’s rubric to your LMS, it will replace the existing rubric.'
                    )}
                </Text>
            </TitleStack>
            <CustomDialogContent device={device}>
                <Stack direction={'row'} alignItems={'flex-start'} spacing={1}>
                    <Checkbox
                        checked={checked || requiresRubricSync}
                        disabled={requiresRubricSync}
                        onChange={() => setChecked((current) => !current)}
                    />
                    <Stack sx={{ paddingTop: 3 }} spacing={2}>
                        <Text weight={'medium'}>
                            {t('rubric.push-lms.sync-tg-rubric', 'Sync TimelyGrader rubric to LMS')}
                        </Text>
                        <CustomBox>
                            <CustomText weight={'regular'}>
                                <Text component={'span'} color={'inherit'} weight={'medium'}>
                                    {t('common:ui.note', 'NOTE').toUpperCase()}:{' '}
                                </Text>
                                {requiresRubricSync
                                    ? t(
                                          'rubric.push-lms.unscored-difference-warning',
                                          'This unscored rubric must be synced before rubric ratings can be passed back because there is no overall numeric grade fallback.'
                                      )
                                    : canUseCompletionFallback
                                      ? t(
                                            'rubric.push-lms.completion-difference-warning',
                                            "If you don't sync this rubric with your LMS, only each student's Complete, Incomplete, or Excused status will be passed back."
                                        )
                                      : t(
                                            'rubric.push-lms.difference-warning',
                                            'If you don’t sync this rubric with your LMS, only the overall final grade will be passed back for each student.'
                                        )}
                            </CustomText>
                        </CustomBox>
                    </Stack>
                </Stack>
            </CustomDialogContent>
            <BottomStack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                <Button variant={'tertiary'} color={'neutral'} onClick={handleClose}>
                    {t('common:controls.cancel', 'Cancel')}
                </Button>
                <Button loading={loading} loadingGrow={loading} disabled={loading} onClick={handleClickSave}>
                    {checked || requiresRubricSync
                        ? t('common:controls.confirm-and-proceed', 'Confirm & Proceed')
                        : t('common:controls.skip-and-proceed', 'Skip & Proceed')}
                </Button>
            </BottomStack>
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '600px',
            height: '332px',
        },
    };
});

const TitleStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(7),
    };
});

const BottomStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(1, 7, 0, 0),
    };
});

const CustomDialogContent = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(0, 7, 7, 7),
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '8px',
        padding: theme.spacing(2, 3),
        background: theme.palette.ensightful.warning[100],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        color: theme.palette.ensightful.warning[900],
    };
});

export default PushRubricDialog;
